import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { SpeechRecognition } from "@capacitor-community/speech-recognition";
import { DEFAULT_SPEECH_LANG, type SpeechLangCode } from "@/lib/speech-languages";

type WebRec = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
};

type WebRecCtor = new () => WebRec;

function getWebRecCtor(): WebRecCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  return (w.SpeechRecognition || w.webkitSpeechRecognition) as WebRecCtor | undefined ?? null;
}

export interface UseSpeechRecognitionOpts {
  lang?: SpeechLangCode;
}

export interface UseSpeechRecognitionResult {
  isSupported: boolean;
  isRecording: boolean;
  transcript: string;
  interim: string;
  error: string | null;
  start: () => void;
  stop: () => void;
}

export function useSpeechRecognition(opts: UseSpeechRecognitionOpts = {}): UseSpeechRecognitionResult {
  const lang = opts.lang || DEFAULT_SPEECH_LANG;
  const isNative = Capacitor.isNativePlatform();

  const [isSupported, setIsSupported] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);

  const webRef = useRef<WebRec | null>(null);
  const partialListenerRef = useRef<{ remove: () => Promise<void> } | null>(null);
  const stateListenerRef = useRef<{ remove: () => Promise<void> } | null>(null);
  const userStoppedRef = useRef(false);
  const langRef = useRef(lang);
  langRef.current = lang;

  // Check support on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isNative) {
        try {
          const { available } = await SpeechRecognition.available();
          if (!cancelled) setIsSupported(!!available);
        } catch {
          if (!cancelled) setIsSupported(false);
        }
      } else {
        setIsSupported(!!getWebRecCtor());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNative]);

  const cleanupListeners = useCallback(async () => {
    try {
      await partialListenerRef.current?.remove();
    } catch {
      // noop
    }
    try {
      await stateListenerRef.current?.remove();
    } catch {
      // noop
    }
    partialListenerRef.current = null;
    stateListenerRef.current = null;
  }, []);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    if (isNative) {
      SpeechRecognition.stop().catch(() => undefined);
      cleanupListeners();
      setIsRecording(false);
    } else {
      try {
        webRef.current?.stop();
      } catch {
        // already stopped
      }
    }
  }, [isNative, cleanupListeners]);

  const startNativeOnce = useCallback(async () => {
    await SpeechRecognition.start({
      language: langRef.current,
      partialResults: true,
      popup: false,
      maxResults: 1,
    });
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setTranscript("");
    setInterim("");
    userStoppedRef.current = false;

    if (isNative) {
      try {
        const perm = await SpeechRecognition.checkPermissions();
        if (perm.speechRecognition !== "granted") {
          const req = await SpeechRecognition.requestPermissions();
          if (req.speechRecognition !== "granted") {
            setError("Microphone access denied. Enable in app settings.");
            return;
          }
        }
        // Wire listeners before start
        partialListenerRef.current = await SpeechRecognition.addListener(
          "partialResults",
          (data: { matches: string[] }) => {
            const text = data?.matches?.[0] || "";
            if (text) setInterim(text);
          },
        );
        stateListenerRef.current = await SpeechRecognition.addListener(
          "listeningState",
          (data: { status: "started" | "stopped" }) => {
            if (data.status === "stopped") {
              // Commit interim → transcript on each cycle stop
              setInterim((curr) => {
                if (curr) {
                  setTranscript((prev) => (prev ? prev + " " : "") + curr.trim());
                }
                return "";
              });
              // Auto-restart if user did not manually stop (continuous mode)
              if (!userStoppedRef.current) {
                startNativeOnce().catch(() => {
                  setIsRecording(false);
                  cleanupListeners();
                });
              } else {
                setIsRecording(false);
                cleanupListeners();
              }
            }
          },
        );
        await startNativeOnce();
        setIsRecording(true);
      } catch (e: any) {
        setError(e?.message || "Failed to start mic.");
        setIsRecording(false);
        cleanupListeners();
      }
      return;
    }

    // Web fallback (PWA on desktop browser)
    const Ctor = getWebRecCtor();
    if (!Ctor) {
      setError("Voice not supported on this device.");
      return;
    }
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e: any) => {
      let finalText = "";
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const txt = res[0]?.transcript || "";
        if (res.isFinal) finalText += txt;
        else interimText += txt;
      }
      if (finalText) {
        setTranscript((prev) => (prev ? prev + " " : "") + finalText.trim());
        setInterim("");
      } else {
        setInterim(interimText);
      }
    };
    rec.onerror = (e: any) => {
      const code = e?.error || "unknown";
      const map: Record<string, string> = {
        "not-allowed": "Microphone access denied.",
        "service-not-allowed": "Voice service unavailable.",
        "no-speech": "No speech detected. Try again.",
        network: "Voice unavailable offline.",
        aborted: "",
        "audio-capture": "No microphone found.",
      };
      const msg = map[code] ?? `Voice error: ${code}`;
      if (msg) setError(msg);
      setIsRecording(false);
    };
    rec.onend = () => {
      setIsRecording(false);
      setInterim("");
    };
    try {
      rec.start();
      webRef.current = rec;
      setIsRecording(true);
    } catch (e: any) {
      setError(e?.message || "Failed to start mic.");
      setIsRecording(false);
    }
  }, [isNative, lang, cleanupListeners, startNativeOnce]);

  useEffect(() => {
    return () => {
      if (isNative) {
        SpeechRecognition.stop().catch(() => undefined);
        cleanupListeners();
      } else {
        try {
          webRef.current?.abort();
        } catch {
          // noop
        }
        webRef.current = null;
      }
    };
  }, [isNative, cleanupListeners]);

  return { isSupported, isRecording, transcript, interim, error, start, stop };
}
